import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import BsModal from 'react-bootstrap/Modal';
import { FaFileContract, FaInfoCircle } from 'react-icons/fa';
import CustomTypeahead from '../CustomTypeahead';
import assetService from '../../services/asset.service';
import debtService from '../../services/debt.service';
import { useLabels } from '../../hooks/useLabels';
import { toast } from '../../toasts';
import { getError, showCurrency } from '../../utils';
import { Button, controlClass } from '../EntityList';
import { USER_ROLES } from '../../constants/roles';

const toInputDate = (value) => (value ? String(value).slice(0, 10) : '');

/**
 * Whether the repair action is offered: super admins, on an alquileres debt
 * whose contract is missing (never linked, or deleted — the populate then
 * yields null). The endpoint enforces the same rule.
 */
export const canRegenerateContract = (debt, userInfo) =>
	userInfo?.role === USER_ROLES.SUPER_ADMIN && debt?.concept === 'alquileres' && !debt?.contract;

const assetLabel = (asset) => [asset.name, asset.owner?.name && `(${asset.owner.name})`].filter(Boolean).join(' ');

/**
 * Super-admin repair: rebuild the contract of an alquileres debt that lost it
 * (POST /debts/:id/regenerate-contract). The contract is built from the debt's
 * own data; only the property and the period are asked for.
 */
function RegenerateContractModal({ show, debt, onClose, onDone }) {
	const { t } = useLabels();
	const [assets, setAssets] = useState([]);
	const [property, setProperty] = useState(null);
	const [periodInit, setPeriodInit] = useState('');
	const [periodEnd, setPeriodEnd] = useState('');
	const [saving, setSaving] = useState(false);
	const submitting = useRef(false);

	useEffect(() => {
		if (!show) return;
		setProperty(null);
		setPeriodInit(toInputDate(debt?.date));
		setPeriodEnd(toInputDate(debt?.endDate));
		let cancelled = false;
		assetService.list({ slim: true })
			.then(({ data }) => { if (!cancelled) setAssets(Array.isArray(data) ? data : []); })
			.catch((err) => { if (!cancelled) toast.error(getError(err)); });
		return () => { cancelled = true; };
	}, [show, debt?._id, debt?.date, debt?.endDate]);

	const canSubmit = Boolean(property?._id) && Boolean(periodInit) && !saving;

	const handleSubmit = async () => {
		if (!canSubmit || submitting.current) return;
		submitting.current = true;
		setSaving(true);
		try {
			await debtService.regenerateContract(debt._id, { property: property._id, periodInit, periodEnd });
			toast.success(t('msg.contractRegenerated'));
			onDone();
		} catch (err) {
			toast.error(getError(err));
		} finally {
			submitting.current = false;
			setSaving(false);
		}
	};

	const summary = [
		{ label: t('entity.tenant'), value: debt?.owner?.name },
		{ label: t('field.amount'), value: debt?.amount != null ? `${showCurrency(debt.currency) || debt.currency || ''} ${Number(debt.amount).toLocaleString('es-UY')}` : '' },
		{ label: t('field.currency'), value: debt?.currency },
	].filter((row) => row.value);

	return (
		<BsModal
			show={show}
			onHide={saving ? undefined : onClose}
			centered
			backdrop={saving ? 'static' : true}
			keyboard={!saving}
			dialogClassName='tw:max-w-[560px]'
			contentClassName='entity-list-page el-modal tw:rounded-[14px] tw:border-0 tw:overflow-hidden tw:shadow-overlay tw:font-sans tw:text-[14px]/[1.45] tw:text-ink-0'
		>
			<BsModal.Header closeButton={!saving} className='tw:items-start tw:border-b tw:border-border tw:px-[22px] tw:pt-[20px] tw:pb-[14px]'>
				<div className='tw:flex tw:items-start tw:gap-3'>
					<span className='tw:mt-0.5 tw:flex tw:h-[38px] tw:w-[38px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[9px] tw:bg-accent-soft tw:text-accent'>
						<FaFileContract />
					</span>
					<div>
						<BsModal.Title className='tw:text-[17px]/[1.3] tw:font-bold tw:tracking-[-0.01em] tw:text-ink-0'>
							{t('title.regenerateContract')}
						</BsModal.Title>
						<p className='tw:mb-0 tw:mt-1 tw:text-[12.5px] tw:text-ink-2'>{debt?.customId}</p>
					</div>
				</div>
			</BsModal.Header>

			<BsModal.Body className='tw:flex tw:flex-col tw:gap-3.5 tw:px-[22px] tw:py-[18px]'>
				<div className='tw:flex tw:items-start tw:gap-2.5 tw:rounded-[7px] tw:border tw:border-accent tw:bg-accent-soft tw:px-3 tw:py-2.5 tw:text-[12px] tw:text-ink-1'>
					<span className='tw:mt-0.5 tw:shrink-0 tw:text-accent'><FaInfoCircle /></span>
					<span>{t('msg.regenerateContractIntro')}</span>
				</div>

				{summary.length > 0 && (
					<div className='tw:overflow-hidden tw:rounded-token tw:border tw:border-border tw:bg-surface-2'>
						{summary.map((row) => (
							<div key={row.label} className='tw:flex tw:items-center tw:gap-2 tw:border-0 tw:border-b tw:border-border tw:px-3 tw:py-2 tw:last:border-b-0'>
								<span className='tw:flex-1 tw:text-[12.5px] tw:text-ink-3'>{row.label}</span>
								<span className='tw:text-[13px] tw:text-ink-0'>{row.value}</span>
							</div>
						))}
					</div>
				)}

				<div>
					<label htmlFor='regenerate-property' className='tw:mb-1.5 tw:block tw:text-[12.5px] tw:text-ink-1'>
						{t('field.property')} *
					</label>
					<CustomTypeahead
						id='regenerate-property'
						labelKey={assetLabel}
						onChange={(selected) => setProperty(selected?.[0] || null)}
						options={assets}
						placeholder={t('field.property')}
						selected={property ? [property] : []}
						disabled={saving}
					/>
				</div>

				<div className='tw:grid tw:grid-cols-2 tw:gap-3'>
					<div>
						<label htmlFor='regenerate-period-init' className='tw:mb-1.5 tw:block tw:text-[12.5px] tw:text-ink-1'>
							{t('field.periodInit')} *
						</label>
						<input
							id='regenerate-period-init'
							type='date'
							value={periodInit}
							onChange={(event) => setPeriodInit(event.target.value)}
							disabled={saving}
							className={`${controlClass} tw:w-full tw:px-2.5`}
						/>
					</div>
					<div>
						<label htmlFor='regenerate-period-end' className='tw:mb-1.5 tw:block tw:text-[12.5px] tw:text-ink-1'>
							{t('field.periodEnd')}
						</label>
						<input
							id='regenerate-period-end'
							type='date'
							value={periodEnd}
							onChange={(event) => setPeriodEnd(event.target.value)}
							disabled={saving}
							className={`${controlClass} tw:w-full tw:px-2.5`}
						/>
					</div>
				</div>
			</BsModal.Body>

			<BsModal.Footer className='tw:border-t tw:border-border tw:bg-surface-2 tw:px-[22px] tw:py-[14px] tw:gap-2'>
				<Button variant='default' onClick={onClose} disabled={saving}>
					{t('action.cancel')}
				</Button>
				<Button
					variant='primary'
					icon={<FaFileContract />}
					disabled={!canSubmit}
					className={!canSubmit ? 'tw:opacity-50' : ''}
					onClick={handleSubmit}
				>
					{saving ? t('msg.regeneratingContract') : t('action.regenerateContract')}
				</Button>
			</BsModal.Footer>
		</BsModal>
	);
}

RegenerateContractModal.propTypes = {
	show: PropTypes.bool.isRequired,
	debt: PropTypes.shape({
		_id: PropTypes.string,
		customId: PropTypes.string,
		amount: PropTypes.number,
		currency: PropTypes.string,
		date: PropTypes.string,
		endDate: PropTypes.string,
		owner: PropTypes.shape({ name: PropTypes.string }),
	}),
	onClose: PropTypes.func.isRequired,
	onDone: PropTypes.func.isRequired,
};

export default RegenerateContractModal;
